"use client";

import { unwrapResult } from "@reduxjs/toolkit";
import { useRouter, useSearchParams } from "next/navigation";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";

import { getQuoteReviewSummaryEditSection } from "@/app/common-utils/edit-quote-review-summary-flow.utils";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { CoverEdit, DestinationEdit } from "@/config/app-constant";
import { Routes } from "@/config/page-view-constants";
import { ANNUAL_MULTI_TRIP, PRODUCT_DOMAIN } from "@/constants";
import { useQuestionsState } from "@/providers/questions-state-provider";
import { useAppDispatch } from "@/store/hook";
import { RootState } from "@/store/root-reducer";
import {
  saveQuestionDataBFF,
  sessionCheck,
  updateQuoteOnQuestion,
} from "@/store/slices/quote/quote.action";
import { setQuestionData } from "@/store/slices/quote/quote.slice";
import { QuotePayload } from "@/types/quote/quote-data";
import {
  findFCDOCountries,
  getSubmitButtonState,
} from "@/utils/question-location-to.utils";

import { useCountryInfo } from "./country-infor-provider";

const initialPayload: QuotePayload = {
  type: "IntermediateData",
  channel: "ONLINE",
  attributes: {
    domain: PRODUCT_DOMAIN,
  },
};

interface LocationToContextType {
  buttonDisable: boolean;
  setButtonDisable: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmitButton: () => Promise<void>;
  isSubmitBtnClicked: boolean;
  setIsSubmitBtnClicked: (clicked: boolean) => void;
  buttonLoading: boolean;
  setButtonLoading: (loading: boolean) => void;
  payload: QuotePayload;
  isOpenUnsavedChangesModal: boolean;
  setIsOpenUnsavedChangesModal: React.Dispatch<React.SetStateAction<boolean>>;
  setPayload: (payload: QuotePayload) => void;
  handleBackButton: () => void;
  setSelectedFrom: React.Dispatch<React.SetStateAction<string | undefined>>;
  scrollRefArray: {
    ref: React.RefObject<HTMLDivElement | null>;
    isToggleWrapper?: boolean;
  }[];
  toggleWrapperRef: React.RefObject<HTMLDivElement | null>;
  isFCDOModalOpen: boolean;
  setIsFCDOModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedFCDOCountryCode: string | undefined;
  setSelectedFCDOCountryCode: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
}

const LocationToContext = createContext<LocationToContextType>({
  buttonDisable: true,
  setButtonDisable: () => {},
  handleSubmitButton: () => Promise.resolve(),
  isSubmitBtnClicked: false,
  setIsSubmitBtnClicked: () => {},
  buttonLoading: false,
  setButtonLoading: () => {},
  payload: initialPayload,
  setPayload: () => {},
  isOpenUnsavedChangesModal: false,
  handleBackButton: () => {},
  setIsOpenUnsavedChangesModal: () => {},
  setSelectedFrom: () => {},
  scrollRefArray: [],
  toggleWrapperRef: { current: null } as React.RefObject<HTMLDivElement | null>,
  isFCDOModalOpen: false,
  setIsFCDOModalOpen: () => {},
  selectedFCDOCountryCode: undefined,
  setSelectedFCDOCountryCode: () => {},
});

export const useLocationTo = () => useContext(LocationToContext);

function LocationToProvider({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const [buttonDisable, setButtonDisable] = useState<boolean>(true);

  const [isSubmitBtnClicked, setIsSubmitBtnClicked] = useState<boolean>(false);
  const [isOpenUnsavedChangesModal, setIsOpenUnsavedChangesModal] =
    useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [payload, setPayload] = useState<QuotePayload>(initialPayload);
  const [isFCDOModalOpen, setIsFCDOModalOpen] = useState<boolean>(false);
  const [selectedFCDOCountryCode, setSelectedFCDOCountryCode] = useState<
    string | undefined
  >(undefined);

  const router = useRouter();
  const searchParams = useSearchParams();
  const editPage = searchParams.get(DestinationEdit);
  const coverEdit = searchParams.get(CoverEdit);
  const dispatch = useAppDispatch();
  const application = process.env.NEXT_PUBLIC_APPLICATION;
  const quoteResponse = useSelector((state: RootState) => state.quote);
  const policyType = quoteResponse?.attributes?.tripDetails?.policyType;
  const { selected } = useQuestionsState();
  const { current } = useWizard();
  const [selectedFrom, setSelectedFrom] = useState<string | undefined>("");
  const toggleWrapperRef = useRef<HTMLDivElement>(null);
  const { restrictedCountry } = useCountryInfo();

  const scrollRefArray = useMemo(() => {
    return [
      {
        ref: toggleWrapperRef,
        isToggleWrapper:
          quoteResponse.attributes?.tripDetails?.policyType ===
          ANNUAL_MULTI_TRIP,
      },
    ];
  }, [quoteResponse.attributes?.tripDetails?.policyType, toggleWrapperRef]);

  useEffect(() => {
    const initialPayloadData: QuotePayload = {
      ...initialPayload,
      ...quoteResponse,
      attributes: {
        ...initialPayload.attributes,
        ...quoteResponse?.attributes,
        tripDetails: {
          ...quoteResponse?.attributes?.tripDetails,
        },
      },
    };
    setPayload(initialPayloadData);
  }, [quoteResponse]);

  useEffect(() => {
    const buttonState = getSubmitButtonState(
      policyType ?? "",
      (selected as string) || "",
      current.uiElementControls?.locationFrom ?? false,
      selectedFrom
    );
    setButtonDisable(buttonState);
  }, [policyType, quoteResponse, selected, selectedFrom, current]);

  const handleBackButton = useCallback(() => {
    if (application === "renewal") {
      setIsOpenUnsavedChangesModal(true);
      return;
    }

    dispatch(setQuestionData(payload));
    let path = current.prev ?? "";
    if (coverEdit) {
      // Check if user came from quote-review (sessionStorage is set)
      const editSection = getQuoteReviewSummaryEditSection();
      if (editSection === "cover") {
        // User came from quote-review, navigate to quote-review
        path = current.edges?.coverEditQuoteSummaryPrev ?? "";
      } else {
        // User came from quote page, use old flow
        path = current.edges?.coverEditPrev ?? "";
      }
    }
    router.push(path);
  }, [application, dispatch, payload, current, coverEdit, router]);

  const handleEditPageSubmit = useCallback(async () => {
    const result = await dispatch(
      updateQuoteOnQuestion({
        payload: { ...payload, viewPage: Routes.TravellingTo },
        dispatch,
        router,
      })
    );
    const originalPromiseResult = unwrapResult(result);

    if (application === "renewal" || originalPromiseResult) {
      router.push(current.edges?.destinationEditNext ?? "");
    }
  }, [application, current, dispatch, payload, router]);

  const handleCoverEditSubmit = useCallback(() => {
    let nextPage = current.edges?.coverEditNext;

    if (coverEdit) {
      // Check if user came from quote-review (sessionStorage is set)
      const editSection = getQuoteReviewSummaryEditSection();
      if (editSection === "cover") {
        // User came from quote-review, use new flow
        nextPage = current?.edges?.coverEditQuoteSummaryNext; // → /cruise/?coverEdit=true
      } else {
        // User came from quote page, use old flow
        nextPage = current?.edges?.coverEditNext; // → /travel-dates/?coverEdit=true
      }
    }

    router.push(nextPage ?? "");
  }, [
    coverEdit,
    current.edges?.coverEditNext,
    current.edges?.coverEditQuoteSummaryNext,
    router,
  ]);

  const handleNewQuoteSubmit = useCallback(async () => {
    const questionData = await dispatch(
      saveQuestionDataBFF({
        payload: { ...payload, viewPage: Routes.TravellingTo },
        dispatch,
        router,
      })
    );
    const originalPromiseResult = unwrapResult(questionData);

    if (originalPromiseResult) {
      router.push(current.next ?? "");
    }
  }, [current, dispatch, payload, router]);

  const handleSessionCheck = useCallback(() => {
    dispatch(sessionCheck({ dispatch, router }));
    router.push(current.next ?? "");
  }, [dispatch, router, current]);

  const handleSubmitButton = useCallback(async () => {
    if (buttonDisable) return;

    if (editPage === "true" && payload?.attributes?.tripDetails?.toLocation) {
      const toLocation = payload.attributes.tripDetails.toLocation;
      const countryCodes = toLocation.split(",");

      let restrictedCountryCode: string | undefined;
      const hasRestrictedCountry = countryCodes.some((countryCode) => {
        const foundRestricted = findFCDOCountries(
          countryCode.trim(),
          restrictedCountry
        );
        if (foundRestricted) {
          restrictedCountryCode = countryCode.trim();
        }
        return foundRestricted;
      });

      if (hasRestrictedCountry) {
        setSelectedFCDOCountryCode(restrictedCountryCode);
        setIsFCDOModalOpen(true);
        return;
      }
    }

    setIsSubmitBtnClicked(true);
    setButtonLoading(true);

    try {
      dispatch(setQuestionData(payload));

      if (editPage && quoteResponse?.referenceId) {
        await handleEditPageSubmit();
      } else if (coverEdit && quoteResponse?.referenceId) {
        handleCoverEditSubmit();
      } else if (!quoteResponse?.referenceId) {
        await handleNewQuoteSubmit();
      } else {
        handleSessionCheck();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setButtonLoading(false);
    }
  }, [
    buttonDisable,
    dispatch,
    payload,
    editPage,
    quoteResponse?.referenceId,
    coverEdit,
    restrictedCountry,
    handleEditPageSubmit,
    handleCoverEditSubmit,
    handleNewQuoteSubmit,
    handleSessionCheck,
  ]);

  const value = useMemo(
    () => ({
      buttonDisable,
      setButtonDisable,
      handleSubmitButton,
      isSubmitBtnClicked,
      setIsSubmitBtnClicked,
      buttonLoading,
      setButtonLoading,
      payload,
      setPayload,
      isOpenUnsavedChangesModal,
      handleBackButton,
      setIsOpenUnsavedChangesModal,
      setSelectedFrom,
      scrollRefArray,
      toggleWrapperRef,
      isFCDOModalOpen,
      setIsFCDOModalOpen,
      selectedFCDOCountryCode,
      setSelectedFCDOCountryCode,
    }),
    [
      buttonDisable,
      buttonLoading,
      handleBackButton,
      handleSubmitButton,
      isOpenUnsavedChangesModal,
      isSubmitBtnClicked,
      payload,
      scrollRefArray,
      isFCDOModalOpen,
      selectedFCDOCountryCode,
    ]
  );

  return (
    <LocationToContext.Provider value={value}>
      {children}
    </LocationToContext.Provider>
  );
}
export default LocationToProvider;
